// @ts-nocheck
import type { JsonValue } from '../../types/primitives';
import { LOCAL_STATE_POLICY, type LocalStateErrorCode } from './localStatePolicy';

/** In-memory session bags keyed by item id (DEC-061). */
const sessions = new Map<number, Record<string, unknown>>();

/** Monotonic revision per item — bumps on successful session writes (display invalidation). */
const revisions = new Map<number, number>();

/** Per-item listeners notified when session revision bumps. */
const revisionListeners = new Map<number, Set<() => void>>();

function bumpLocalStateRevision(itemId: number): void {
  const next = (revisions.get(itemId) ?? 0) + 1;
  revisions.set(itemId, next);
  const listeners = revisionListeners.get(itemId);
  if (listeners) {
    for (const listener of listeners) {
      listener();
    }
  }
}

function bumpLocalStateRevisionForBag(bag: Record<string, unknown>): void {
  for (const [itemId, session] of sessions.entries()) {
    if (session === bag) {
      bumpLocalStateRevision(itemId);
      return;
    }
  }
}

export function getLocalStateRevision(itemId: number): number {
  return revisions.get(itemId) ?? 0;
}

export function subscribeLocalStateRevision(
  itemId: number,
  listener: () => void,
): () => void {
  let listeners = revisionListeners.get(itemId);
  if (!listeners) {
    listeners = new Set();
    revisionListeners.set(itemId, listeners);
  }
  listeners.add(listener);
  return () => {
    const current = revisionListeners.get(itemId);
    if (!current) {
      return;
    }
    current.delete(listener);
    if (current.size === 0) {
      revisionListeners.delete(itemId);
    }
  };
}

export type LocalStateWriteResult =
  | { ok: true; changed: boolean }
  | { ok: false; code: LocalStateErrorCode; message: string };

export type LocalStateKeyResult =
  | { ok: true }
  | { ok: false; code: LocalStateErrorCode; message: string };

function utf8ByteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  return unescape(encodeURIComponent(value)).length;
}

function estimateValueBytes(value: unknown): number {
  if (typeof value === 'string') {
    return utf8ByteLength(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value == null) {
    return 8;
  }
  try {
    return utf8ByteLength(JSON.stringify(value) ?? '');
  } catch {
    return 64;
  }
}

function estimateBagBytes(bag: Record<string, unknown>): number {
  let total = 0;
  for (const [key, value] of Object.entries(bag)) {
    total += utf8ByteLength(key) + estimateValueBytes(value);
  }
  return total;
}

export function validateLocalStateKey(key: string): LocalStateKeyResult {
  if (!key || typeof key !== 'string' || !key.trim()) {
    return { ok: false, code: 'invalid_key', message: 'Local state key must be non-empty.' };
  }
  if (key.length > LOCAL_STATE_POLICY.maxKeyLength) {
    return {
      ok: false,
      code: 'invalid_key',
      message: `Local state key exceeds ${LOCAL_STATE_POLICY.maxKeyLength} characters.`,
    };
  }
  if (!LOCAL_STATE_POLICY.keyPattern.test(key)) {
    return {
      ok: false,
      code: 'invalid_key',
      message: `Local state key '${key}' is invalid (allowed: A-Z a-z 0-9 _ . -).`,
    };
  }
  return { ok: true };
}

/**
 * Returns the mutable session bag for an item, creating it from defaults on first access.
 * Defaults are only applied when the session does not yet exist.
 */
export function getOrCreateLocalStateSession(
  itemId: number,
  defaults?: Readonly<Record<string, JsonValue>> | null,
): Record<string, unknown> {
  const existing = sessions.get(itemId);
  if (existing) {
    return existing;
  }
  const bag: Record<string, unknown> = {};
  if (defaults != null) {
    for (const [key, value] of Object.entries(defaults)) {
      if (validateLocalStateKey(key).ok) {
        bag[key] = value;
      }
    }
  }
  sessions.set(itemId, bag);
  return bag;
}

/** Returns the session bag if present; does not create. */
export function peekLocalStateSession(itemId: number): Record<string, unknown> | undefined {
  return sessions.get(itemId);
}

export function clearLocalStateSession(itemId: number): void {
  if (sessions.has(itemId)) {
    bumpLocalStateRevision(itemId);
  }
  sessions.delete(itemId);
}

/** Test helper — clears all sessions. */
export function clearAllLocalStateSessions(): void {
  sessions.clear();
  revisions.clear();
  revisionListeners.clear();
}

export function readLocalStateValue(
  bag: Record<string, unknown>,
  key: string,
): { found: boolean; value?: unknown } {
  if (!Object.prototype.hasOwnProperty.call(bag, key)) {
    return { found: false };
  }
  return { found: true, value: bag[key] };
}

export function writeLocalStateValue(
  bag: Record<string, unknown>,
  key: string,
  value: unknown,
): LocalStateWriteResult {
  const keyCheck = validateLocalStateKey(key);
  if (!keyCheck.ok) {
    return keyCheck;
  }

  if (typeof value === 'string' && utf8ByteLength(value) > LOCAL_STATE_POLICY.maxStringBytes) {
    return {
      ok: false,
      code: 'limit_string',
      message: `Local state string exceeds ${LOCAL_STATE_POLICY.maxStringBytes} bytes.`,
    };
  }

  const isNewKey = !Object.prototype.hasOwnProperty.call(bag, key);
  if (isNewKey && Object.keys(bag).length >= LOCAL_STATE_POLICY.maxKeys) {
    return {
      ok: false,
      code: 'limit_keys',
      message: `Local state exceeds max ${LOCAL_STATE_POLICY.maxKeys} keys.`,
    };
  }

  const previous = bag[key];
  bag[key] = value;
  if (estimateBagBytes(bag) > LOCAL_STATE_POLICY.maxTotalBytes) {
    if (isNewKey) {
      delete bag[key];
    } else {
      bag[key] = previous;
    }
    return {
      ok: false,
      code: 'limit_total',
      message: `Local state exceeds max ${LOCAL_STATE_POLICY.maxTotalBytes} total bytes.`,
    };
  }

  const changed = previous !== value;
  if (changed) {
    bumpLocalStateRevisionForBag(bag);
  }
  return { ok: true, changed };
}

export function deleteLocalStateValue(
  bag: Record<string, unknown>,
  key: string,
): LocalStateWriteResult {
  const keyCheck = validateLocalStateKey(key);
  if (!keyCheck.ok) {
    return keyCheck;
  }
  if (!Object.prototype.hasOwnProperty.call(bag, key)) {
    return { ok: true, changed: false };
  }
  delete bag[key];
  bumpLocalStateRevisionForBag(bag);
  return { ok: true, changed: true };
}
